import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getResources } from '../services/dataService';

// Placeholder for a Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <nav className="mt-8 flex justify-center">
      <ul className="inline-flex items-center -space-x-px">
        <li>
          <button
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
          >
            Previous
          </button>
        </li>
        {pageNumbers.map(number => (
          <li key={number}>
            <button
              onClick={() => onPageChange(number)}
              className={`px-3 py-2 leading-tight border border-gray-300 ${currentPage === number ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700' : 'text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700'}`}
            >
              {number}
            </button>
          </li>
        ))}
        <li>
          <button
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
};

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [coursesError, setCoursesError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination state (example)
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage] = useState(6); // Show 6 courses per page

  const fetchCourses = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoadingCourses(true);
      // In a real scenario, getCourses might support pagination parameters (page, limit)
      const courseData = await getResources('Course');
      setCourses(courseData || []);
      setCoursesError(null);
    } catch (error) {
      console.error("Failed to fetch courses:", error);
      setCoursesError(error.message || 'Could not fetch courses.');
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Filter courses based on search term
  const filteredCourses = useMemo(() => {
    if (!searchTerm) {
      return courses;
    }
    return courses.filter(course => {
      const { name, code, name_en, name_vi } = course.properties;
      const term = searchTerm.toLowerCase();
      return (
        (name?.toLowerCase() || '').includes(term) ||
        (code?.toLowerCase() || '').includes(term) ||
        (name_en?.toLowerCase() || '').includes(term) ||
        (name_vi?.toLowerCase() || '').includes(term)
      );
    });
  }, [courses, searchTerm]);

  // Pagination logic
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses.slice(indexOfFirstCourse, indexOfLastCourse);
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (!currentUser && !loadingCourses) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-xl text-gray-700">Please log in to view the dashboard.</p>
        {/* Optionally, add a link to the login page here */}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <header className="mb-8">
        <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center bg-white p-6 shadow-md rounded-lg">
          {currentUser ? (
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Màn hình chính</h1>
              <p className="text-gray-600">Xin chào, {currentUser.email}!</p>
            </div>
          ) : (
            <h1 className="text-3xl font-bold text-gray-800">Màn hình chính</h1>
          )}
          {currentUser && (
            <button 
              onClick={logout}
              className="mt-4 sm:mt-0 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg shadow hover:shadow-md transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
            >
              Logout
            </button>
          )}
        </div>
      </header>

      <main className="container mx-auto">
        {currentUser ? (
          <section id="user-info" className="mb-8 p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">Thông tin người dùng</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="text-lg text-gray-800">{currentUser.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Role</p>
                <p className="text-lg text-gray-800">{currentUser.role || 'N/A'}</p>
              </div>
            </div>
            {/* Admin-only navigation */}
            {currentUser.role === 'admin' && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-700 mb-2">Admin Navigation</h3>
                <div className="flex flex-wrap gap-3">
                  <Link 
                    to="/admin/users" 
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    User Management
                  </Link>
                </div>
              </div>
            )}
          </section>
        ) : (
          <div className="flex justify-center items-center h-64">
            <p className="text-xl text-gray-500">Loading user data...</p>
          </div>
        )}

        {currentUser && (
          <section id="courses-list" className="p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-2xl font-semibold text-gray-700 mb-6">Danh sách khóa học</h2>
            <div className="flex justify-between items-center mb-4">
              <div className="mb-6">
                <input 
                  type="text"
                  placeholder="Tìm kiếm khóa học..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on new search
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            {loadingCourses && (
              <div className="flex justify-center items-center h-40">
                <p className="text-lg text-gray-500">Loading courses...</p> {/* Consider a spinner component */}
              </div>
            )}
            {coursesError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
                <p className="font-bold">Error</p>
                <p>{coursesError}</p>
              </div>
            )}
            {!loadingCourses && !coursesError && currentCourses.length === 0 && (
              <div className="text-center py-10">
                <p className="text-lg text-gray-500">No courses found.</p>
                {/* Optionally, suggest an action, e.g., 'Try refreshing or contact support.' */}
              </div>
            )}
            {!loadingCourses && !coursesError && currentCourses.length > 0 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentCourses.map((course) => (
                    <Link to={`/course/${course.id}/graph`} key={course.id} className="block bg-gray-50 p-5 shadow-lg rounded-lg hover:shadow-xl hover:ring-2 hover:ring-blue-500 transition-all duration-300 ease-in-out">
                      <h3 className="text-xl font-semibold text-blue-600 mb-2 truncate" title={course.properties.name || 'Unnamed Course'}>{course.properties.name || 'Unnamed Course'}</h3>
                      {course.properties.description && (
                        <p className="text-gray-700 mt-2">{course.properties.description}</p>
                      )}
                      {/* Add more course details here if needed, e.g., description, prerequisites */}
                    </Link>
                  ))}
                </div>
                <Pagination 
                  currentPage={currentPage} 
                  totalPages={totalPages} 
                  onPageChange={handlePageChange} 
                />
              </>
            )}
          </section>
        )}
      </main>

      <footer className="mt-12 py-6 text-center text-gray-500 text-sm">
        <p>&copy; {new Date().getFullYear()} Quản lý khóa học. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Dashboard;
